import React, { useState, useEffect, useCallback } from 'react';
import {
  Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, Bar, ReferenceLine, ComposedChart
} from 'recharts';
import CandlestickChart from './CandlestickChart';
import './Dashboard.css';

function Dashboard({ api, onLogout }) {
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [indicators, setIndicators] = useState([]);
  const [prices, setPrices] = useState([]);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const [chartDays, setChartDays] = useState(30);
  const [chartInterval, setChartInterval] = useState('daily');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [stocksRes, indicatorsRes, signalsRes] = await Promise.all([
        api.get('/api/stocks'),
        api.get('/api/indicators'),
        api.get('/api/signals')
      ]);
      setStocks(stocksRes.data);
      setIndicators(indicatorsRes.data);
      setSignals(signalsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Database may be empty or unavailable.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchStockDetails = useCallback(async (symbol, days = chartDays, interval = chartInterval) => {
    try {
      const [indicatorsRes, pricesRes] = await Promise.all([
        api.get(`/api/stocks/${symbol}/indicators`),
        api.get(`/api/stocks/${symbol}/prices?days=${days}&interval=${interval}`)
      ]);
      setIndicators(indicatorsRes.data);
      setPrices(pricesRes.data);
    } catch (err) {
      console.error('Error fetching stock details:', err);
    }
  }, [api, chartDays, chartInterval]);

  const handleStockSelect = (stock) => {
    setSelectedStock(stock);
    fetchStockDetails(stock.symbol);
    setActiveTab('chart');
  };

  const handleTimeframeChange = (days, interval) => {
    setChartDays(days);
    setChartInterval(interval);
    if (selectedStock) {
      fetchStockDetails(selectedStock.symbol, days, interval);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSignalColor = (signal) => {
    if (signal === 'BUY' || signal === 'STRONG_BUY') return '#4ade80';
    if (signal === 'SELL' || signal === 'STRONG_SELL') return '#f87171';
    return '#fbbf24';
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>Stock Indicators Dashboard</h1>
          <span className="subtitle">Trading Metrics Testing</span>
        </div>
        <div className="header-right">
          <button onClick={fetchData} className="refresh-btn">
            Refresh
          </button>
          <button onClick={onLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </header>

      <nav className="dashboard-nav">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'stocks' ? 'active' : ''}
          onClick={() => setActiveTab('stocks')}
        >
          Stocks
        </button>
        <button
          className={activeTab === 'signals' ? 'active' : ''}
          onClick={() => setActiveTab('signals')}
        >
          Signals
        </button>
        {selectedStock && (
          <button
            className={activeTab === 'chart' ? 'active' : ''}
            onClick={() => setActiveTab('chart')}
          >
            {selectedStock.symbol} Chart
          </button>
        )}
      </nav>

      <main className="dashboard-content">
        {error && (
          <div className="error-banner">
            {error}
            <p className="error-hint">
              The database tables may need to be populated with stock data.
            </p>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="overview-grid">
            <div className="stat-card">
              <h3>Tracked Stocks</h3>
              <div className="stat-value">{stocks.length}</div>
            </div>
            <div className="stat-card">
              <h3>Active Indicators</h3>
              <div className="stat-value">{indicators.length}</div>
            </div>
            <div className="stat-card">
              <h3>Recent Signals</h3>
              <div className="stat-value">{signals.length}</div>
            </div>
            <div className="stat-card">
              <h3>Buy Signals</h3>
              <div className="stat-value green">
                {signals.filter(s => s.signal_type === 'BUY' || s.signal_type === 'STRONG_BUY').length}
              </div>
            </div>

            <div className="recent-signals-card">
              <h3>Latest Signals</h3>
              {signals.length === 0 ? (
                <p className="empty-state">No signals yet</p>
              ) : (
                <div className="signals-list">
                  {signals.slice(0, 10).map((signal, idx) => (
                    <div key={idx} className="signal-item">
                      <span className="signal-symbol">{signal.symbol}</span>
                      <span
                        className="signal-type"
                        style={{ color: getSignalColor(signal.signal_type) }}
                      >
                        {signal.signal_type}
                      </span>
                      <span className="signal-time">{formatDate(signal.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {indicators.length > 0 && (
              <div className="indicators-chart-card">
                <h3>Recent Indicator Values</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={indicators.slice(0, 20).reverse()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="calculated_at" tickFormatter={formatDate} stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1f2937', border: 'none' }}
                      labelFormatter={formatDate}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="rsi"
                      stroke="#8b5cf6"
                      fill="rgba(139, 92, 246, 0.3)"
                      name="RSI"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stocks' && (
          <div className="stocks-view">
            <h2>Tracked Stocks</h2>
            {stocks.length === 0 ? (
              <p className="empty-state">No stocks being tracked yet</p>
            ) : (
              <div className="stocks-grid">
                {stocks.map((stock) => (
                  <div
                    key={stock.id}
                    className="stock-card"
                    onClick={() => handleStockSelect(stock)}
                  >
                    <div className="stock-symbol">{stock.symbol}</div>
                    <div className="stock-name">{stock.name}</div>
                    {stock.last_price && (
                      <div className="stock-price">${parseFloat(stock.last_price).toFixed(2)}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'signals' && (
          <div className="signals-view">
            <h2>Trade Signals</h2>
            {signals.length === 0 ? (
              <p className="empty-state">No trade signals generated yet</p>
            ) : (
              <div className="signals-table-container">
                <table className="signals-table">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Signal</th>
                      <th>Confidence</th>
                      <th>Price</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {signals.map((signal, idx) => (
                      <tr key={idx}>
                        <td className="symbol-cell">{signal.symbol}</td>
                        <td>
                          <span
                            className="signal-badge"
                            style={{ backgroundColor: getSignalColor(signal.signal_type) }}
                          >
                            {signal.signal_type}
                          </span>
                        </td>
                        <td>{signal.confidence ? `${(signal.confidence * 100).toFixed(1)}%` : '-'}</td>
                        <td>{signal.price ? `$${parseFloat(signal.price).toFixed(2)}` : '-'}</td>
                        <td>{formatDate(signal.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'chart' && selectedStock && (
          <div className="chart-view">
            <div className="chart-header">
              <h2>{selectedStock.symbol} - {selectedStock.name}</h2>
              {selectedStock.last_price && (
                <div className="current-price">
                  ${parseFloat(selectedStock.last_price).toFixed(2)}
                </div>
              )}
            </div>

            <div className="chart-container candlestick-section">
              <CandlestickChart
                prices={prices}
                symbol={selectedStock.symbol}
                onTimeframeChange={handleTimeframeChange}
              />
            </div>

            {indicators.length > 0 && (
              <div className="indicators-grid">
                {/* RSI Chart */}
                <div className="chart-container indicator-chart">
                  <div className="indicator-header">
                    <h3>RSI (Relative Strength Index)</h3>
                    <span className="indicator-value">
                      {indicators[0]?.rsi ? parseFloat(indicators[0].rsi).toFixed(2) : '-'}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={indicators.slice().reverse()}>
                      <defs>
                        <linearGradient id="rsiGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="calculated_at" tickFormatter={formatDate} stroke="#9ca3af" fontSize={10} />
                      <YAxis stroke="#9ca3af" domain={[0, 100]} ticks={[0, 30, 50, 70, 100]} fontSize={10} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                        labelFormatter={formatDate}
                        formatter={(value) => [parseFloat(value).toFixed(2), 'RSI']}
                      />
                      <ReferenceLine y={70} stroke="#f87171" strokeDasharray="3 3" label={{ value: 'Overbought', fill: '#f87171', fontSize: 10 }} />
                      <ReferenceLine y={30} stroke="#4ade80" strokeDasharray="3 3" label={{ value: 'Oversold', fill: '#4ade80', fontSize: 10 }} />
                      <ReferenceLine y={50} stroke="#6b7280" strokeDasharray="2 2" />
                      <Area type="monotone" dataKey="rsi" stroke="#8b5cf6" fill="url(#rsiGradient)" strokeWidth={2} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="indicator-info">
                    <span className="info-item oversold">Below 30 = Oversold</span>
                    <span className="info-item overbought">Above 70 = Overbought</span>
                  </div>
                  <SignalStatus status={getRSIStatus(indicators[0]?.rsi)} />
                </div>

                {/* MACD Chart */}
                <div className="chart-container indicator-chart">
                  <div className="indicator-header">
                    <h3>MACD (Moving Average Convergence Divergence)</h3>
                    <span className="indicator-value">
                      {indicators[0]?.macd ? parseFloat(indicators[0].macd).toFixed(4) : '-'}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={indicators.slice().reverse()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="calculated_at" tickFormatter={formatDate} stroke="#9ca3af" fontSize={10} />
                      <YAxis stroke="#9ca3af" fontSize={10} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                        labelFormatter={formatDate}
                        formatter={(value, name) => [parseFloat(value).toFixed(4), name]}
                      />
                      <Legend />
                      <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="2 2" />
                      <Bar
                        dataKey="macd_histogram"
                        name="Histogram"
                        fill="#6366f1"
                        opacity={0.7}
                      />
                      <Line type="monotone" dataKey="macd" stroke="#ec4899" name="MACD" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="signal_line" stroke="#14b8a6" name="Signal" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="indicator-info">
                    <span className="info-item macd-bull">MACD above Signal = Bullish</span>
                    <span className="info-item macd-bear">MACD below Signal = Bearish</span>
                  </div>
                  <SignalStatus status={getMACDStatus(indicators[0]?.macd, indicators[0]?.signal_line)} />
                </div>

                {/* Bollinger Bands Info */}
                <div className="chart-container indicator-chart">
                  <div className="indicator-header">
                    <h3>Bollinger Bands</h3>
                    <span className="indicator-value">
                      {indicators[0]?.bb_middle ? `$${parseFloat(indicators[0].bb_middle).toFixed(2)}` : '-'}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={indicators.slice().reverse()}>
                      <defs>
                        <linearGradient id="bbGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="calculated_at" tickFormatter={formatDate} stroke="#9ca3af" fontSize={10} />
                      <YAxis stroke="#9ca3af" domain={['auto', 'auto']} fontSize={10} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                        labelFormatter={formatDate}
                        formatter={(value) => [`$${parseFloat(value).toFixed(2)}`, '']}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="bb_upper" stroke="#f87171" fill="transparent" name="Upper Band" strokeWidth={1} dot={false} />
                      <Area type="monotone" dataKey="bb_middle" stroke="#fbbf24" fill="url(#bbGradient)" name="Middle (SMA20)" strokeWidth={2} dot={false} />
                      <Area type="monotone" dataKey="bb_lower" stroke="#4ade80" fill="transparent" name="Lower Band" strokeWidth={1} dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                  <div className="indicator-info">
                    <span className="info-item bb-upper">Upper: ${indicators[0]?.bb_upper ? parseFloat(indicators[0].bb_upper).toFixed(2) : '-'}</span>
                    <span className="info-item bb-lower">Lower: ${indicators[0]?.bb_lower ? parseFloat(indicators[0].bb_lower).toFixed(2) : '-'}</span>
                  </div>
                  <SignalStatus status={getBBStatus(prices[0]?.close, indicators[0]?.bb_upper, indicators[0]?.bb_lower, indicators[0]?.bb_middle)} />
                </div>

                {/* VWAP Chart */}
                <div className="chart-container indicator-chart">
                  <div className="indicator-header">
                    <h3>VWAP (Volume Weighted Avg Price)</h3>
                    <span className="indicator-value">
                      {prices.length > 0 ? `$${calculateVWAP(prices).toFixed(2)}` : '-'}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={calculateVWAPSeries(prices)}>
                      <defs>
                        <linearGradient id="vwapGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="time" tickFormatter={formatDate} stroke="#9ca3af" fontSize={10} />
                      <YAxis stroke="#9ca3af" domain={['auto', 'auto']} fontSize={10} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                        labelFormatter={formatDate}
                        formatter={(value, name) => [`$${parseFloat(value).toFixed(2)}`, name]}
                      />
                      <Legend />
                      <Area type="monotone" dataKey="close" stroke="#6b7280" fill="transparent" name="Close" strokeWidth={1} dot={false} />
                      <Line type="monotone" dataKey="vwap" stroke="#f59e0b" name="VWAP" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <div className="indicator-info">
                    <span className="info-item vwap-above">Price above VWAP = Bullish</span>
                    <span className="info-item vwap-below">Price below VWAP = Bearish</span>
                  </div>
                  <SignalStatus status={getVWAPStatus(prices, calculateVWAP(prices))} />
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// Signal Status Component
function SignalStatus({ status }) {
  const statusConfig = {
    good: { label: 'BULLISH', color: '#4ade80', bg: 'rgba(74, 222, 128, 0.15)', icon: '▲' },
    bad: { label: 'BEARISH', color: '#f87171', bg: 'rgba(248, 113, 113, 0.15)', icon: '▼' },
    neutral: { label: 'NEUTRAL', color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.15)', icon: '●' },
  };

  const config = statusConfig[status] || statusConfig.neutral;

  return (
    <div className="signal-status" style={{ backgroundColor: config.bg, borderColor: config.color }}>
      <span className="signal-icon" style={{ color: config.color }}>{config.icon}</span>
      <span className="signal-label" style={{ color: config.color }}>{config.label}</span>
      <span className="signal-description" style={{ color: config.color }}>
        {status === 'good' && 'Favorable conditions'}
        {status === 'bad' && 'Unfavorable conditions'}
        {status === 'neutral' && 'No clear signal'}
      </span>
    </div>
  );
}

// RSI Status: < 30 = oversold (good buy), > 70 = overbought (bad/sell), else neutral
function getRSIStatus(rsi) {
  if (!rsi) return 'neutral';
  const value = parseFloat(rsi);
  if (value <= 30) return 'good';      // Oversold - potential buy
  if (value >= 70) return 'bad';       // Overbought - potential sell
  if (value >= 45 && value <= 55) return 'neutral';
  if (value > 55) return 'good';       // Above midpoint - bullish momentum
  return 'bad';                         // Below midpoint - bearish momentum
}

// MACD Status: MACD > Signal = bullish (good), MACD < Signal = bearish (bad)
function getMACDStatus(macd, signal) {
  if (!macd || !signal) return 'neutral';
  const macdVal = parseFloat(macd);
  const signalVal = parseFloat(signal);
  const diff = macdVal - signalVal;

  if (Math.abs(diff) < 0.01) return 'neutral';  // Very close = neutral
  if (diff > 0) return 'good';                   // MACD above signal = bullish
  return 'bad';                                  // MACD below signal = bearish
}

// Bollinger Bands Status: Price near lower = good (oversold), near upper = bad (overbought)
function getBBStatus(close, upper, lower, middle) {
  if (!close || !upper || !lower || !middle) return 'neutral';
  const price = parseFloat(close);
  const upperBand = parseFloat(upper);
  const lowerBand = parseFloat(lower);
  const middleBand = parseFloat(middle);

  const range = upperBand - lowerBand;
  const position = (price - lowerBand) / range;  // 0 = at lower, 1 = at upper

  if (position <= 0.2) return 'good';            // Near lower band - oversold, potential buy
  if (position >= 0.8) return 'bad';             // Near upper band - overbought, potential sell
  if (price > middleBand) return 'good';         // Above middle - bullish
  if (price < middleBand) return 'bad';          // Below middle - bearish
  return 'neutral';
}

// VWAP Status: Price > VWAP = bullish (good), Price < VWAP = bearish (bad)
function getVWAPStatus(prices, vwap) {
  if (!prices || prices.length === 0 || !vwap) return 'neutral';
  const latestPrice = parseFloat(prices[0]?.close);

  const diff = ((latestPrice - vwap) / vwap) * 100;  // Percentage difference

  if (Math.abs(diff) < 0.5) return 'neutral';        // Within 0.5% = neutral
  if (diff > 0) return 'good';                        // Price above VWAP = bullish
  return 'bad';                                       // Price below VWAP = bearish
}

// Calculate current VWAP value
function calculateVWAP(prices) {
  if (!prices || prices.length === 0) return 0;

  let cumulativeTPV = 0; // cumulative (typical price * volume)
  let cumulativeVolume = 0;

  prices.forEach(p => {
    const high = parseFloat(p.high);
    const low = parseFloat(p.low);
    const close = parseFloat(p.close);
    const volume = parseInt(p.volume);

    const typicalPrice = (high + low + close) / 3;
    cumulativeTPV += typicalPrice * volume;
    cumulativeVolume += volume;
  });

  return cumulativeVolume > 0 ? cumulativeTPV / cumulativeVolume : 0;
}

// Calculate VWAP series for charting
function calculateVWAPSeries(prices) {
  if (!prices || prices.length === 0) return [];

  const sortedPrices = [...prices].sort((a, b) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  let cumulativeTPV = 0;
  let cumulativeVolume = 0;

  return sortedPrices.map(p => {
    const high = parseFloat(p.high);
    const low = parseFloat(p.low);
    const close = parseFloat(p.close);
    const volume = parseInt(p.volume);

    const typicalPrice = (high + low + close) / 3;
    cumulativeTPV += typicalPrice * volume;
    cumulativeVolume += volume;

    const vwap = cumulativeVolume > 0 ? cumulativeTPV / cumulativeVolume : 0;

    return {
      time: p.timestamp,
      close: close,
      vwap: vwap,
    };
  });
}

export default Dashboard;
